import { faChevronDown, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ClickAwayListener } from '@material-ui/core';
import clsx from 'clsx';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';
import styles from './WaitlistPickerField.module.scss';

export interface WaitlistPickerOption {
    id: string;
    label: string;
}

interface WaitlistPickerFieldProps {
    label: string;
    value: string;
    /** "No preference" option first ("ASAP", "Anytime", "Ongoing"), then the ones that show the picker. */
    options: Array<WaitlistPickerOption>;
    activeOption: string;
    /** "calendar" opens a wide popover (a full-screen sheet on mobile); "list" a narrow one (times). */
    variant?: 'calendar' | 'list';
    align?: 'left' | 'right';
    className?: string;
    onSelectOption: (id: string, close: () => void) => void;
    /** Picker shown next to the options (calendar, times); receives a close callback. */
    renderPicker: (close: () => void) => ReactNode;
}

// Pill field (same look as the other selects) that opens a popover with the options on the
// left ("ASAP" / "Select Date") and the picker on the right.
export const WaitlistPickerField: FunctionComponent<WaitlistPickerFieldProps> = ({
    label,
    value,
    options,
    activeOption,
    variant = 'calendar',
    align = 'left',
    className,
    onSelectOption,
    renderPicker
}) => {
    const [open, setOpen] = useState(false);
    const popoverRef = useRef<HTMLDivElement>(null);

    const close = () => setOpen(false);

    // Fields near the bottom of the drawer: bring the whole popover into view.
    useEffect(() => {
        open && popoverRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }, [open, activeOption]);

    return (
        <div className={clsx(styles.root, className)}>
            <Label>{label}</Label>

            <ClickAwayListener onClickAway={close}>
                <div className={styles.wrapper}>
                    <button
                        type="button"
                        className={clsx(styles.field, { [styles.fieldOpen]: open })}
                        aria-expanded={open}
                        onClick={() => setOpen(current => !current)}
                    >
                        <span className={styles.value}>{value}</span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={styles.chevron}
                        />
                    </button>

                    {open && (
                        <div
                            ref={popoverRef}
                            role="dialog"
                            aria-label={label}
                            className={clsx(styles.popover, {
                                [styles.popoverList]: variant === 'list',
                                [styles.popoverCalendar]: variant === 'calendar',
                                [styles.popoverRight]: align === 'right'
                            })}
                        >
                            {/* Only visible on the mobile sheet. */}
                            <div className={styles.sheetHeader}>
                                <p className={styles.sheetTitle}>{label}</p>
                                <button
                                    type="button"
                                    className={styles.sheetClose}
                                    aria-label="Close"
                                    onClick={close}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            </div>

                            <div
                                className={styles.options}
                                role="radiogroup"
                            >
                                {options.map(option => (
                                    <button
                                        key={option.id}
                                        type="button"
                                        role="radio"
                                        aria-checked={option.id === activeOption}
                                        className={clsx(styles.option, {
                                            [styles.optionSelected]: option.id === activeOption
                                        })}
                                        onClick={() => onSelectOption(option.id, close)}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>

                            {activeOption !== options[0].id && (
                                <div className={styles.picker}>{renderPicker(close)}</div>
                            )}
                        </div>
                    )}
                </div>
            </ClickAwayListener>
        </div>
    );
};

export default WaitlistPickerField;
